import { useEffect, useMemo, useState } from 'react';

import { Box, Button, IconButton, styled, Typography } from '@mui/joy';
import {
  IoCalendarOutline,
  IoClose,
  IoCompassOutline,
  IoGitPullRequestOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { usePermissionsContext } from '../../context/permissionsContext';
import { track } from '../../services/analytics';
import useFetchPlatformBanner from '../../services/strapi/useFetchPlatformBanner';
import { userState } from '../../stores/user';
import { brand } from '../../theme/brand';
import { matchesBannerAudience } from '../../utils/functions/matchesBannerAudience';

const BANNER_DISMISSED_MAP = 'platform_banner_dismissed';

const checkBannerDismissed = (
  userEmail: string | undefined,
  bannerId: number,
  updatedAt: string
): boolean => {
  if (!userEmail) return false;

  try {
    const storedData = localStorage.getItem(BANNER_DISMISSED_MAP);
    if (!storedData) return false;

    const dismissalMap = JSON.parse(storedData);
    const dismissalKey = `${userEmail}_${bannerId}_${updatedAt}`;
    return dismissalMap[dismissalKey] === true;
  } catch (error) {
    console.error('Error reading banner dismissal from localStorage:', error);
    return false;
  }
};

const BannerContainer = styled(Box)<{ colorTheme: 'purple' | 'blue' | 'grey' }>(({
  colorTheme,
}) => {
  const colorMap = {
    purple: brand.common.purple,
    blue: brand.ocean['400'],
    grey: brand.neutral['500'],
  };

  return {
    backgroundColor: colorMap[colorTheme],
    borderRadius: '100px',
    padding: '6px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    maxWidth: '1360px',
    position: 'relative',
  };
});

const BannerContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
});

const TextContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  color: 'white',
  flex: 1,
});

const RightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexShrink: 0,
});

const iconMap = {
  'calendar-outline': IoCalendarOutline,
  'git-pull-request-outline': IoGitPullRequestOutline,
  'compass-outline': IoCompassOutline,
};

interface PlatformBannerProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const PlatformBanner = ({ onVisibilityChange }: PlatformBannerProps) => {
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userState);
  const { isFreemiumUser, isCompassEnabled } = usePermissionsContext();
  const { data: banner, isLoading } = useFetchPlatformBanner();

  const userEmail = userInfo?.email;
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was dismissed by this user (from localStorage)
  const wasDismissed = useMemo(() => {
    if (!userEmail || !banner) return false;
    return checkBannerDismissed(userEmail, banner.id, banner.updatedAt);
  }, [userEmail, banner]);

  const handleDismiss = () => {
    if (!userEmail || !banner) return;

    setIsDismissed(true);

    try {
      const storedData = localStorage.getItem(BANNER_DISMISSED_MAP);
      const dismissalMap = storedData ? JSON.parse(storedData) : {};
      const dismissalKey = `${userEmail}_${banner.id}_${banner.updatedAt}`;

      dismissalMap[dismissalKey] = true;
      localStorage.setItem(BANNER_DISMISSED_MAP, JSON.stringify(dismissalMap));

      track('banner_dismissed', 'platform_banner', {
        banner_id: banner.id,
        banner_title: banner.title,
        target_audience: banner.target_audience,
      });
    } catch (error) {
      console.error('Error saving banner dismissal to localStorage:', error);
    }
  };

  const handleCtaClick = () => {
    if (!banner) return;

    track('banner_cta_clicked', 'platform_banner', {
      banner_id: banner.id,
      banner_title: banner.title,
      button_text: banner.button_text,
      button_url: banner.button_url,
      target_audience: banner.target_audience,
    });

    if (banner.button_url.startsWith('http')) {
      window.open(banner.button_url, '_blank');
    } else {
      navigate(banner.button_url);
    }
  };

  const shouldShow = useMemo(() => {
    if (isLoading || !banner || wasDismissed || isDismissed) return false;
    if (!banner.is_visible) return false;

    const matchesAudience = matchesBannerAudience(
      banner.target_audience,
      isFreemiumUser,
      isCompassEnabled
    );

    return matchesAudience;
  }, [banner, isLoading, wasDismissed, isDismissed, isFreemiumUser, isCompassEnabled]);

  useEffect(() => {
    if (shouldShow && banner) {
      track('banner_viewed', 'platform_banner', {
        banner_id: banner.id,
        banner_title: banner.title,
        target_audience: banner.target_audience,
      });
    }
    onVisibilityChange?.(shouldShow);
  }, [shouldShow, banner, onVisibilityChange]);

  if (!shouldShow || !banner) return null;

  const IconComponent = banner.icon_name ? iconMap[banner.icon_name] : null;

  return (
    <BannerContainer colorTheme={banner.color_theme}>
      <BannerContent>
        <LeftSection>
          {IconComponent && <IconComponent size={24} color='white' style={{ flexShrink: 0 }} />}
          <TextContainer>
            <Typography
              sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: 1.42,
                color: 'white',
              }}
            >
              {banner.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: 1.5,
                color: 'white',
              }}
            >
              {banner.subtitle}
            </Typography>
          </TextContainer>
        </LeftSection>
        <RightSection>
          <Button
            size='sm'
            variant='outlined'
            onClick={handleCtaClick}
            sx={{
              backgroundColor: '#fbfcfe',
              borderColor: '#e5e5e5',
              color: '#171a1c',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '14px',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderColor: '#e5e5e5',
              },
            }}
          >
            {banner.button_text}
          </Button>
          <IconButton
            size='sm'
            variant='plain'
            onClick={handleDismiss}
            sx={{
              color: 'white',
              minWidth: '24px',
              minHeight: '24px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <IoClose size={16} />
          </IconButton>
        </RightSection>
      </BannerContent>
    </BannerContainer>
  );
};
